import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check } from 'lucide-react';

const InclusionItem = ({ image, icon, title, description }) => (
  <div className="flex gap-4 items-start">
    <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
      <img src={image} alt={title} className="w-full h-full object-cover" />
    </div>
    <div className="flex-grow">
      <div className="flex items-center gap-2 mb-1">
        <img src={icon} alt="" className="w-6 h-6" />
        <h4 className="font-display text-foreground">{title}</h4>
      </div>
      <p className="text-muted-foreground font-body text-sm">{description}</p>
    </div>
  </div>
);

export const InclusionsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const inclusions = [
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_femme_qui_recoit_des_energies_dorees_qui_sintegrent__b633b178-af91-422c-9c18-d8971ca73b3e-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/10-100x100.png",
      title: "14 transmissions delphiques",
      description: "(pour te libérer de toutes tes couches de souffrance)"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_soin_energetique_initiation_avec_les_dauphins_multid_ddb7b2e6-aefb-4fd2-ab23-66cea1ff416d-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/13-100x100.png",
      title: "14 méditations",
      description: "(pour recevoir une guidance personnelle)"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_femme_qui_recoit_des_energies_dorees_qui_sintegrent__9ab8d035-d85d-4dc1-b435-138f01165e0e-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/11-100x100.png",
      title: "14 vidéos d'intégration",
      description: "(pour ancrer en toi ces énergies)"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/04/DALL%C2%B7E-2024-05-07-15.51.32-Create-a-digital-artwork-depicting-the-concept-of-collective-consciousness-transcending-individual-minds.-The-image-should-illustrate-an-expansive-et-300x300.webp",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/16-100x100.png",
      title: "Des mantras et géométries sacrées",
      description: "pour recharger ton énergie ou dynamiser ton eau"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/LIVRET-DACCOMPAGNEMENT-POUR-LES-14-FREQUENCES-DAUPHINS-LES-3-FREQUENCES-BALEINE-RAIE-BELUGA-212x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/12-100x100.png",
      title: "Des carnets d'exercices de transformation",
      description: "(avec des mantras, géométries sacrée…)"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_guerisseur___de_10_protocoles_energetiques_a_realise_cb1606b1-708b-4568-b712-718e757a81cc-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/18-1-100x100.png",
      title: "Une trousse à outils de guérisseur",
      description: "+ de 10 protocoles énergétiques à réaliser"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/MG_3292-300x200.jpg",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/19-1-100x100.png",
      title: "Guide du tirage d'oracle",
      description: "(pour tirer les cartes comme un·e pro)"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_femme_qui_recoit_des_energies_dorees_qui_sintegrent__02c18cbc-3a7e-4d32-ad68-4fd7dc21ccf9-1-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/16-1-100x100.png",
      title: "Mon soin énergétique à distance",
      description: "pour harmoniser tes énergies"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/live-dauphin-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/20-100x100.png",
      title: "1 live par mois tous ensemble",
      description: "pour échanger et me poser toutes tes questions"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_groupe_facebook_communaute_bienveillante_dechange_et_a7be108c-bcc0-4b2a-b307-f5ec16873c6d-300x300.png",
      icon: "https://gillesdelieuze.com/wp-content/uploads/2024/11/8-100x100.png",
      title: "L'accès à une communauté bienveillante",
      description: "sur le groupe privé Facebook"
    }
  ];

  const bonuses = [
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_renaissance_new_age_galactic_divine_white_gold_multi_7abf6662-243e-4ba3-95d7-b78d395cf370.png",
      title: "Le protocole chamanique des deuils et renaissances",
      value: "149€"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/08/gilles1385_photo_art_ultrarealiste_Manta_ray_8K_cosmic_electric_cf2f4728-073c-4bc5-8622-ea475fb68dac.png",
      title: "Trois transmissions galactiques avec la conscience Baleine, du Béluga et Raie",
      value: "231€"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/04/DALL%C2%B7E-2024-05-07-16.23.02-Create-a-high-resolution-digital-artwork-depicting-the-concept-of-equanimous-presence-with-enhancements.-The-image-should-feature-a-central-serene-fi.webp",
      title: "Le sommet Métamorphose : les ateliers pour oser prendre ta place",
      value: "180€"
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background relative overflow-hidden">
      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Tout ce que tu obtiens
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight">
            <span className="text-gradient-gold">Tout ce que tu obtiens immédiatement</span>
          </h2>
        </motion.div>

        {/* Inclusions Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {inclusions.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="glass-card rounded-xl p-4 hover:border-accent/30 transition-colors"
            >
              <InclusionItem {...item} />
            </motion.div>
          ))}
        </div>

        {/* Bonus Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <h3 className="font-display text-xl text-foreground text-center mb-8">
            <span className="text-accent">3 bonus exclusifs</span> (d'une valeur de 560€) :
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {bonuses.map((bonus, index) => (
              <motion.div
                key={bonus.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="luxury-card-gold rounded-xl p-4"
              >
                <div className="flex items-center gap-4">
                  <img src={bonus.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Check className="w-4 h-4 text-accent" />
                      <span className="text-accent text-sm font-body">{bonus.value}</span>
                    </div>
                    <p className="text-foreground font-body text-sm">{bonus.title}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InclusionsSection;
